import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Updater } from '@tanstack/react-table';
import {
  getColumnSizingStorageKey,
  getColumnSizingChanges,
  readColumnSizing,
  removeDefaultColumnSizing,
  resetAllColumnSizing,
  resetColumnSizing,
  writeColumnSizing,
  type ColumnSizingState,
} from 'utils/table/columnSizing.ts';

interface UseColumnSizingOptions {
  tableId: string;
  agendaId: string;
  viewId: string;
  queryKey: string;
  defaultColumnSize: number;
  persist?: boolean;
}

export const useColumnSizing = ({ tableId, agendaId, viewId, queryKey, defaultColumnSize, persist = true }: UseColumnSizingOptions) => {
  const columnSizingStorageKey = useMemo(
    () => (persist && tableId ? getColumnSizingStorageKey({ tableId, agendaId, viewId, queryKey }) : null),
    [agendaId, queryKey, tableId, viewId, persist]
  );
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>(() =>
    removeDefaultColumnSizing(readColumnSizing(columnSizingStorageKey), defaultColumnSize)
  );
  const columnSizingRef = useRef(columnSizing);
  const manuallySizedColumns = useRef(new Set<string>());
  const columnSizingWriteTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scheduleColumnSizingWrite = useCallback(
    (nextSizing: ColumnSizingState) => {
      if (!persist) return;
      if (columnSizingWriteTimeoutRef.current) {
        clearTimeout(columnSizingWriteTimeoutRef.current);
      }

      columnSizingWriteTimeoutRef.current = setTimeout(() => {
        writeColumnSizing(columnSizingStorageKey, nextSizing);
        columnSizingWriteTimeoutRef.current = null;
      }, 150);
    },
    [columnSizingStorageKey, persist]
  );

  const updateColumnSizing = useCallback(
    (nextSizing: ColumnSizingState) => {
      const changedColumnSizing = persist ? removeDefaultColumnSizing(nextSizing, defaultColumnSize) : nextSizing;
      const previous = columnSizingRef.current;
      if (
        Object.keys(previous).length === Object.keys(changedColumnSizing).length &&
        Object.entries(changedColumnSizing).every(([id, width]) => previous[id] !== undefined && Math.abs(previous[id] - width) < 0.5)
      )
        return;
      columnSizingRef.current = changedColumnSizing;
      setColumnSizing(changedColumnSizing);
      scheduleColumnSizingWrite(changedColumnSizing);
    },
    [defaultColumnSize, scheduleColumnSizingWrite, persist]
  );

  const applyAutomaticColumnSizing = useCallback(
    (measured: ColumnSizingState) => {
      const next = { ...columnSizingRef.current };
      for (const [id, width] of Object.entries(measured)) {
        if (!manuallySizedColumns.current.has(id) && Number.isFinite(width) && width > 0) next[id] = width;
      }
      updateColumnSizing(next);
    },
    [updateColumnSizing]
  );

  const handleColumnSizingChange = useCallback(
    (updaterOrValue: Updater<ColumnSizingState>, renderedSizing?: ColumnSizingState) => {
      const previousSizing = renderedSizing ?? columnSizingRef.current;
      const nextSizing = typeof updaterOrValue === 'function' ? updaterOrValue(previousSizing) : updaterOrValue;
      const changes = renderedSizing ? getColumnSizingChanges(nextSizing, renderedSizing) : nextSizing;

      for (const [id, width] of Object.entries(changes)) {
        if (width !== previousSizing[id]) manuallySizedColumns.current.add(id);
      }

      updateColumnSizing(renderedSizing ? { ...columnSizingRef.current, ...changes } : nextSizing);
    },
    [updateColumnSizing]
  );

  const handleColumnSizingReset = useCallback(
    (columnId: string) => {
      manuallySizedColumns.current.delete(columnId);
      updateColumnSizing(resetColumnSizing(columnSizingRef.current, columnId));
    },
    [updateColumnSizing]
  );

  const handleAllColumnSizingReset = useCallback(() => {
    manuallySizedColumns.current.clear();
    updateColumnSizing(resetAllColumnSizing());
  }, [updateColumnSizing]);

  const hasChangedColumnSizing = useMemo(() => Object.keys(columnSizing).length > 0, [columnSizing]);

  useEffect(() => {
    if (!persist) return;
    const storedColumnSizing = readColumnSizing(columnSizingStorageKey);
    const changedColumnSizing = removeDefaultColumnSizing(storedColumnSizing, defaultColumnSize);
    columnSizingRef.current = changedColumnSizing;
    setColumnSizing(changedColumnSizing);

    if (Object.keys(storedColumnSizing).length !== Object.keys(changedColumnSizing).length) {
      writeColumnSizing(columnSizingStorageKey, changedColumnSizing);
    }
  }, [columnSizingStorageKey, defaultColumnSize, persist]);

  useEffect(() => {
    return () => {
      if (columnSizingWriteTimeoutRef.current) {
        clearTimeout(columnSizingWriteTimeoutRef.current);
      }
      if (persist) writeColumnSizing(columnSizingStorageKey, columnSizingRef.current);
    };
  }, [columnSizingStorageKey, persist]);

  return {
    columnSizing,
    manuallySizedColumns: manuallySizedColumns.current,
    handleColumnSizingChange,
    handleColumnSizingReset,
    handleAllColumnSizingReset,
    hasChangedColumnSizing,
    applyAutomaticColumnSizing,
  };
};
